import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Phone, MapPin, Briefcase, Inbox, CheckCircle2, Filter } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUSES = ["new", "contacted", "qualified", "closed"];

const STATUS_STYLES = {
  new: "border-tratou-green/50 text-tratou-green",
  contacted: "border-blue-400/40 text-blue-300",
  qualified: "border-yellow-300/40 text-yellow-200",
  closed: "border-white/15 text-tratou-faint",
};

export default function AdminDashboard() {
  const { admin, logout, loading } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!admin) { nav("/admin/login", { replace: true }); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, loading]);

  const refresh = async () => {
    setBusy(true); setErr("");
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/stats")]);
      setLeads(l.data); setStats(s.data);
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => l.id === id ? { ...l, status } : l));
      refresh();
    } catch (e) { setErr(formatError(e)); }
  };

  if (loading || !admin) return <div className="min-h-screen flex items-center justify-center text-tratou-dim font-mono">Loading...</div>;

  const visible = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="min-h-screen bg-tratou-void" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/tratou-logo.png" alt="Tratou Energy" className="h-6 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-tratou-dim border-l border-white/10 pl-3">ADMIN</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-tratou-dim hidden sm:inline">{admin.email}</span>
            <button onClick={refresh} className="btn-ghost" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button onClick={() => { logout(); nav("/admin/login"); }} className="btn-secondary !py-2.5 !px-4" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <span className="eyebrow">Operator Console</span>
        <h1 className="heading-section mt-3" style={{ fontSize: "2.5rem" }}>Lead Pipeline</h1>

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10" data-testid="admin-stats">
            <StatCell label="Total" v={stats.total} icon={Inbox} />
            <StatCell label="New" v={stats.new} icon={Inbox} highlight />
            <StatCell label="Contacted" v={stats.contacted} icon={Mail} />
            <StatCell label="Qualified" v={stats.qualified} icon={CheckCircle2} />
            <StatCell label="Closed" v={stats.closed} icon={CheckCircle2} />
          </div>
        )}

        {/* Filter */}
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-tratou-dim" />
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border ${
                filter === s ? "border-tratou-green text-tratou-green bg-tratou-green/5" : "border-white/10 text-tratou-dim hover:border-white/30"
              }`}
              data-testid={`admin-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        {err && <div className="mt-6 panel p-4 text-red-400 text-sm" data-testid="admin-err">{err}</div>}

        <div className="mt-8 panel overflow-x-auto" data-testid="admin-leads-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="px-4 py-3 eyebrow-dim">Date</th>
                <th className="px-4 py-3 eyebrow-dim">Name</th>
                <th className="px-4 py-3 eyebrow-dim">Company</th>
                <th className="px-4 py-3 eyebrow-dim">Contact</th>
                <th className="px-4 py-3 eyebrow-dim">Industry</th>
                <th className="px-4 py-3 eyebrow-dim">Power</th>
                <th className="px-4 py-3 eyebrow-dim">Source</th>
                <th className="px-4 py-3 eyebrow-dim">Status</th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-tratou-dim font-mono">Loading...</td></tr>
              )}
              {!busy && visible.length === 0 && (
                <tr><td colSpan="8" className="px-4 py-10 text-center text-tratou-dim font-mono" data-testid="admin-empty">No leads yet.</td></tr>
              )}
              {!busy && visible.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-4 font-mono text-xs text-tratou-dim">
                    {new Date(l.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-white">{l.name}</p>
                    {l.project_location && <p className="mt-1 flex items-center gap-1 text-tratou-faint text-xs"><MapPin className="w-3 h-3" /> {l.project_location}</p>}
                  </td>
                  <td className="px-4 py-4 text-tratou-dim">{l.company || "—"}</td>
                  <td className="px-4 py-4 text-tratou-dim">
                    <p className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" /> {l.email}</p>
                    {l.phone && <p className="mt-1 flex items-center gap-1 text-xs"><Phone className="w-3 h-3" /> {l.phone}</p>}
                  </td>
                  <td className="px-4 py-4 text-tratou-dim">
                    {l.industry && <p className="flex items-center gap-1 text-xs"><Briefcase className="w-3 h-3" /> {l.industry}</p>}
                  </td>
                  <td className="px-4 py-4 text-tratou-dim text-xs">{l.power_requirement || "—"}</td>
                  <td className="px-4 py-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] border border-white/15 px-2 py-1 text-tratou-dim">{l.source}</span>
                  </td>
                  <td className="px-4 py-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`font-mono text-[10px] uppercase tracking-[0.18em] border px-3 py-1.5 ${STATUS_STYLES[l.status] || ""}`}
                          data-testid={`status-trigger-${l.id}`}
                        >
                          {l.status}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-tratou-deep border border-white/10 rounded-none">
                        {STATUSES.map((s) => (
                          <DropdownMenuItem
                            key={s}
                            onClick={() => updateStatus(l.id, s)}
                            className="font-mono text-xs uppercase tracking-[0.16em] rounded-none focus:bg-tratou-green/10 focus:text-tratou-green"
                            data-testid={`status-set-${l.id}-${s}`}
                          >
                            {s}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function StatCell({ label, v, icon: Icon, highlight }) {
  return (
    <div className="bg-tratou-deep p-5">
      <div className="flex items-center gap-2"><Icon className={`w-4 h-4 ${highlight ? "text-tratou-green" : "text-tratou-dim"}`} /><p className="eyebrow-dim">{label}</p></div>
      <p className={`mt-2 font-display text-3xl font-bold ${highlight ? "text-tratou-green" : "text-white"}`}>{v}</p>
    </div>
  );
}
